import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { TopNameItem } from 'types/tape';
import ListIcon from '@mui/icons-material/List';
import StockIcon from 'components/StockIcon';

interface TopNamesListProps {
  items: TopNameItem[];
  headers?: string[];
  nameHeader?: string;
  maxItems?: number;
  onItemClick: (item: TopNameItem) => void;
}

const TopNamesList: React.FC<TopNamesListProps> = ({
  items,
  headers,
  nameHeader = 'Ticker', // Header for the underlying column, defaults to 'Ticker'
  maxItems,
  onItemClick,
}) => {
  const dynamicHeaders = useMemo(() => {
    if (headers != null && headers.length > 0) {
      return headers;
    }

    const labelSet = new Set<string>();
    items.forEach((item) => {
      item.values.forEach((val) => labelSet.add(val.label));
    });
    return Array.from(labelSet);
  }, [items, headers]);

  const displayedItems = useMemo(
    () => (maxItems == null ? items : items.slice(0, maxItems)),
    [items, maxItems],
  );

  // Handle case when there are no items
  if (items.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ListIcon />
        <Typography>No results</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <TableContainer
        component={Paper}
        elevation={0} // Removes shadow
        sx={{ border: 'none' }}
      >
        <Table size="small" aria-label="top names table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  backgroundColor: 'background.paper',
                  borderBottom: 'none',
                  padding: '6px 12px',
                }}
                align="left"
              >
                {nameHeader}
              </TableCell>
              {dynamicHeaders.map((header: string, index: number) => (
                <TableCell
                  key={`${header}-${index}`}
                  align={header === nameHeader ? 'left' : 'right'}
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.secondary',
                    backgroundColor: 'background.paper',
                    borderBottom: 'none',
                    padding: '6px 12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedItems.map((item: TopNameItem, index: number) => (
              <TopNameTableRow
                key={`${item.underlying}-${index}`}
                item={item}
                headers={dynamicHeaders}
                onItemClick={onItemClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

interface TopNameTableRowProps {
  item: TopNameItem;
  headers: string[];
  onItemClick: (item: TopNameItem) => void;
}

const TopNameTableRow: React.FC<TopNameTableRowProps> = ({
  item,
  headers,
  onItemClick,
}) => {
  const { underlying, values } = item;
  const theme = useTheme();

  // Create a map for quick lookup of values by label, including color
  const valuesMap = useMemo(() => {
    const map = new Map<string, { value: string; color?: string }>();
    values.forEach((val) =>
      map.set(val.label, { value: val.displayValue, color: val.color }),
    );
    return map;
  }, [values]);

  return (
    <TableRow
      onClick={() => onItemClick(item)}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.text.primary, 0.05),
        },
      }}
    >
      <TableCell
        sx={{
          padding: '4px 8px',
          borderBottom: 'none',
        }}
        align="left"
      >
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
            whiteSpace: 'nowrap',
          }}
        >
          <StockIcon symbol={underlying} sx={{ width: 16, height: 16 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontSize: '0.75rem',
            }}
          >
            {underlying}
          </Typography>
        </Stack>
      </TableCell>
      {/* Dynamic Value Cells */}
      {headers.map((header) => {
        const cellData = valuesMap.get(header);
        return (
          <TableCell
            key={header}
            align={header === 'Ticker' ? 'left' : 'right'}
            sx={{
              padding: '4px 8px',
              borderBottom: 'none',
            }}
          >
            {cellData != null ? (
              <Typography
                variant="body2"
                sx={{
                  color: cellData.color ?? 'text.primary',
                  fontSize: '0.75rem',
                  fontWeight: cellData.color != null ? 500 : 'normal',
                  whiteSpace: 'nowrap',
                }}
              >
                {cellData.value}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.75rem' }}
              >
                -
              </Typography>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default TopNamesList;
